import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AlertTriangle } from 'lucide-react';

// CO-814 — one quiet line under a request's status chip, shared by every "my requests" history screen
// (leave, short leave, shift swap, attendance correction, claims, encashment, overtime, shift pay).
//
//  * Approved + autoApproved  → "Auto-approved (…)": nobody reviewed it, so the employee is not told a
//    person did. Status itself stays Approved (older clients render it unchanged).
//  * Pending + pendingWith    → who the request is waiting on when it is NOT the employee's manager.
//    The server fills `pendingWith` only on the requester's own list endpoints; everywhere else it is
//    null and this renders nothing. "Manager" is the normal case and deliberately shows nothing new.
//
// Visual pattern is the original leave-history label (text-xs, muted, no chip) so it reads as a
// footnote to the status, not a second status. Only "Unroutable" carries color + icon: it is the one
// case the employee has to act on (nobody can approve until a manager is assigned).

export type AutoApprovalReason = 'ApprovalBypass' | 'WorkflowAutoApproval' | 'PolicyNoApprovalRequired';
export type PendingWith = 'Manager' | 'Hr' | 'Chief' | 'Unroutable';

interface ApprovalRoutingBadgesProps {
  /** The request is in its approved (final) state for this request type. */
  isApproved: boolean;
  /** The request is still waiting for a decision. */
  isPending: boolean;
  autoApproved?: boolean | null;
  autoApprovalReason?: AutoApprovalReason | string | null;
  pendingWith?: PendingWith | string | null;
  /** Written to data-request-id so Playwright can match a row to the id it created. */
  requestId?: number | string | null;
  className?: string;
  /**
   * Wraps the auto-approved label in an extra element carrying this testid, for specs written before
   * the shared component existed (regular leave history: `leave-auto-approved-label`).
   */
  legacyAutoApprovedTestId?: string;
}

interface UserSlice {
  user?: { role?: string };
}

function autoApprovedKey(reason?: string | null): string {
  if (reason === 'ApprovalBypass') return 'approvalRouting.autoApproved.bypass';
  if (reason === 'PolicyNoApprovalRequired') return 'approvalRouting.autoApproved.policy';
  return 'approvalRouting.autoApproved.workflow';
}

const ApprovalRoutingBadges: React.FC<ApprovalRoutingBadgesProps> = ({
  isApproved,
  isPending,
  autoApproved,
  autoApprovalReason,
  pendingWith,
  requestId,
  className = '',
  legacyAutoApprovedTestId,
}) => {
  const { t } = useTranslation();
  // Primary role string. The sole-HR case is the only one where the wording changes: they are the
  // person who would normally fix a missing manager, so the hint tells them to do it for themselves.
  const role = useSelector((state: UserSlice) => state.user?.role);
  const idAttr = requestId != null ? String(requestId) : undefined;

  if (isApproved && autoApproved) {
    const label = (
      <span
        data-testid="approval-auto-approved-label"
        data-reason={autoApprovalReason ?? undefined}
        data-request-id={idAttr}
        className={`text-xs text-muted-foreground ${className}`}
      >
        {t(autoApprovedKey(autoApprovalReason))}
      </span>
    );
    return legacyAutoApprovedTestId ? <span data-testid={legacyAutoApprovedTestId}>{label}</span> : label;
  }

  if (!isPending || !pendingWith) return null;

  if (pendingWith === 'Hr') {
    return (
      <span data-testid="approval-pending-with-hr" data-request-id={idAttr} className={`text-xs text-muted-foreground ${className}`}>
        {t('approvalRouting.waitingFor.hr')}
      </span>
    );
  }

  if (pendingWith === 'Chief') {
    return (
      <span data-testid="approval-pending-with-chief" data-request-id={idAttr} className={`text-xs text-muted-foreground ${className}`}>
        {t('approvalRouting.waitingFor.chief')}
      </span>
    );
  }

  if (pendingWith === 'Unroutable') {
    const selfHr = role === 'Hr';
    return (
      <span
        data-testid="approval-pending-unroutable"
        data-request-id={idAttr}
        data-self-hr={selfHr ? 'true' : 'false'}
        className={`flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300 ${className}`}
      >
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
        <span>{t(selfHr ? 'approvalRouting.waitingFor.unroutableSelfHr' : 'approvalRouting.waitingFor.unroutable')}</span>
      </span>
    );
  }

  return null;
};

export default ApprovalRoutingBadges;
